import { useCallback } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"

import { queryKeys } from "~/api/cache"
import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"
import { useDoesCurrentUserHavePermissions } from "~/api/hooks/useCurrentUser"
import useSolarProposalConsumption from "~/api/hooks/useSolarProposalConsumption"
import { Drawer } from "~/components/ui-components/v2"
import { GreenbuttonDataDrawer } from "~/containers/Proposal/Setup/GreenbuttonDataDrawer"

import { SOLAR_SETUP_STEP_LABELS, solarProposalPath } from "./solar-setup-paths"
import { personName } from "../BatteryOnlyProposal/person-display"

const APPLY_LOCK_CHECKING = `Checking whether this proposal's selections can change…`
const APPLY_LOCK_UNKNOWN = `Couldn't check whether this proposal's selections can change. Close and reopen Green Button Data to try again.`
const GREEN_BUTTON_NO_SITE_MESSAGE = `Green Button Data isn't available because this proposal isn't linked to a Site.`

/**
 * Green Button Data for a Solar V2 Proposal: the V1 and Battery drawer, in
 * selection-only mode. Connecting, meter selection and the Site's import are
 * shared with V1; applying the data changes only the consumption selection,
 * so savings read out of date until the seller recalculates them.
 */
export const SolarGreenbuttonStep = ({
  onClose,
  onRecalculateSavings,
  proposal,
  proposalId,
}: {
  onClose: () => void
  /**
   * Replaces "go to the savings section": the Summary has no presentation
   * yet, and Confirm & open recalculates savings itself.
   */
  onRecalculateSavings?: () => void
  proposal: SolarProposalDto
  proposalId: string
}): JSX.Element => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const consumption = useSolarProposalConsumption(proposalId)
  const canUseSupportDiagnostics = useDoesCurrentUserHavePermissions([
    `vision_admin`,
  ])

  // The apply already refetched these; the drawer calls this again once it
  // sees the Site's application, so the step's reads agree with it.
  const handleUsageApplied = useCallback(async () => {
    await Promise.all(
      [
        queryKeys.solarProposal.consumption(proposalId),
        queryKeys.solarProposal.byId(proposalId),
        queryKeys.solarProposal.design(proposalId),
        queryKeys.solarProposal.savings(proposalId),
        queryKeys.solarProposal.share(proposalId),
        queryKeys.solarProposal.utility(proposalId),
      ].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey, exact: true }),
      ),
    )
  }, [proposalId, queryClient])

  /** Closes setup and brings the savings section, and its Recalculate, into view. */
  const goToSavings = useCallback((): void => {
    navigate(solarProposalPath(proposalId))
    requestAnimationFrame(() =>
      document.getElementById(`savings`)?.scrollIntoView({ block: `start` }),
    )
  }, [navigate, proposalId])

  // The step fills the viewport and hides the rail, so it always renders a
  // drawer the seller can close.
  if (!proposal.siteId) {
    return (
      <Drawer
        open
        theme="dark"
        onClose={onClose}
        title={SOLAR_SETUP_STEP_LABELS.greenbutton}
      >
        <p className="text-sm text-foreground">{GREEN_BUTTON_NO_SITE_MESSAGE}</p>
      </Drawer>
    )
  }

  // Until the financing lock is known, applying stays off rather than assumed.
  const applyLockedReason = consumption.data
    ? consumption.data.financingLockReason
    : consumption.isError
      ? APPLY_LOCK_UNKNOWN
      : APPLY_LOCK_CHECKING

  return (
    <GreenbuttonDataDrawer
      applyLockedReason={applyLockedReason}
      applyMode="selection_only"
      canUseSupportDiagnostics={canUseSupportDiagnostics}
      homeownerEmail={proposal.homeowner?.email ?? ``}
      homeownerName={
        proposal.homeowner
          ? personName(proposal.homeowner, `the homeowner`)
          : `the homeowner`
      }
      onClose={onClose}
      onRecalculateSavings={onRecalculateSavings ?? goToSavings}
      onUsageApplied={handleUsageApplied}
      proposalId={proposalId}
      siteId={proposal.siteId}
      theme="dark"
      utilityCompanyName={proposal.utilityName ?? ``}
    />
  )
}
